<template>
  <div :class="className">
    <Combobox
      v-model="selectedOption"
      :name="name"
      :multiple="multiple"
      as="div"
      by="id"
      class="mt-px"
      @update:model-value="handleUpdate"
    >
      <ComboboxLabel v-if="label" class="block text-body-dark font-semibold text-sm leading-none mb-3 mt-0.25">
        {{ label }}&nbsp;<span v-if="required" class="text-red-500">*</span>
      </ComboboxLabel>
      <div class="relative mt-1">
        <div
          class="relative w-full cursor-default overflow-hidden text-left focus:outline-none"
          :aria-label="ariaLabel"
        >
          <ComboboxInput
            ref="inputField"
            :class="[ 'px-4 flex items-center w-full appearance-none transition duration-300 ease-in-out text-bolder text-sm focus:outline-none focus:ring-0 mt-0.5', shadow ? 'focus:shadow' : '', variantClasses[variant], sizeClasses[dimension], disabled ? 'bg-gray-100 cursor-not-allowed' : '', error ? '!border-red-500' : '', inputClassName, classBorder ]"
            :display-value="inputDisplayValue"
            :placeholder="multiple && selectedOption.length && !isFocus ? '' : placeholder"
            @change="query = $event.target.value"
            @keydown="handleType"
            @keyup.enter="addOptionValue"
            @focus="handleFocus"
            @blur="handleBlur"
            @click="handleClick"
          />
          <div
            v-if="multiple && selectedOption.length && !isFocus"
            aria-hidden="true"
            class="pointer-events-none absolute inset-y-0 start-4 end-8 flex translate-y-0.5 items-center overflow-hidden"
          >
            <span class="flex h-[26px] min-w-0 items-center truncate rounded-full bg-primary-dark px-3 text-xs font-bold text-light">
              {{ displayValue(selectedOption[0]) }}
            </span>
            <span v-if="selectedOption.length > 1" class="ms-2 flex-none text-xs font-bold text-bolder">
              +{{ selectedOption.length - 1 }}
            </span>
          </div>
          <ComboboxButton
            class="absolute inset-y-0 right-0 flex items-center pr-2"
            @click="handleToggle"
          >
            <ChevronUpDownIcon
              class="h-4 w-4 text-gray-400 mt-0.5"
              aria-hidden="true"
            />
          </ComboboxButton>
        </div>
        <div v-show="isOpen">
          <ComboboxOptions
            ref="optionsField"
            :class="[ 'absolute z-10 mt-1 max-h-60 w-full overflow-auto bg-white text-sm', shadow ? 'focus:shadow' : '', variantClasses[variant], disabled ? 'bg-gray-100 cursor-not-allowed' : '', 'border rounded-lg' ]"
            static
          >
            <ul
              v-if="!allowCustomValue && filteredOptions.length === 0 && query !== ''"
            >
              <li
                class="relative cursor-default select-none py-2"
                :class="{
                  'pl-10 pr-4': showSelectedCheck,
                  'px-4': !showSelectedCheck,
                  'text-dark': true,
                }"
              >
                <span
                  class="block whitespace-nowrap"
                  :class="{ 'font-medium': true, 'truncate': showSelectedCheck }"
                >
                  {{ $t('text-no-option-found') }}
                </span>
              </li>
            </ul>

            <ul
              v-if="allowCustomValue && filteredOptions.length === 0 && query !== ''"
            >
              <li
                class="relative cursor-pointer select-none py-2"
                :class="{
                  'pl-10 pr-4': showSelectedCheck,
                  'px-4': !showSelectedCheck,
                  'text-dark': true,
                }"
                @click="addOptionValue"
              >
                <span
                  class="block whitespace-nowrap"
                  :class="{ 'font-medium': true, 'truncate': showSelectedCheck }"
                >
                  {{ $t(addOptionLabel, { value: query }) }}
                </span>
              </li>
            </ul>
            <ComboboxOption
              v-for="option in filteredOptions"
              :key="option.id"
              v-slot="{ selected, active }"
              as="ul"
              :value="option"
              @click="handleSelect"
            >
              <li
                class="relative cursor-pointer select-none py-2"
                :class="{
                  'pl-10 pr-4': showSelectedCheck,
                  'px-4': !showSelectedCheck,
                  'bg-accent text-white': active,
                  'text-dark': !active,
                }"
              >
                <span
                  class="block whitespace-nowrap"
                  :class="{ 'font-medium': selected, 'font-normal': !selected, 'truncate': showSelectedCheck }"
                >
                  <slot name="option" :option="option">
                    {{ option.title || option.name }}
                  </slot>
                </span>
                <span
                  v-if="selected && showSelectedCheck"
                  class="absolute inset-y-0 left-0 flex items-center pl-3"
                  :class="{ 'text-white': active, 'text-accent': !active }"
                >
                  <CheckIcon class="h-4 w-4" aria-hidden="true" />
                </span>
              </li>
            </ComboboxOption>
          </ComboboxOptions>
        </div>
      </div>
    </Combobox>
    <p v-if="error" class="my-2 text-xs text-red-500">
        {{ error }}
    </p>
  </div>
</template>

<script setup>
import _ from 'lodash-es';
import { Combobox, ComboboxLabel, ComboboxInput, ComboboxButton, ComboboxOptions, ComboboxOption } from '@headlessui/vue';
import CheckIcon from "@components/icons/check-icon";
import ChevronUpDownIcon from "@components/icons/chevron-up-down-icon";

const variantClasses = {
  normal:
    "bg-gray-100 border-border-base focus:shadow focus:bg-light focus:border-accent",
  solid:
    "bg-gray-100 border-border-100 focus:bg-light focus:border-accent",
  outline: "border-border-base focus:border-accent",
  line: "ps-0 border-b border-border-base rounded-none focus:border-accent",
};

const sizeClasses = {
  tiny: "text-xs h-10",
  small: "text-sm h-10",
  medium: "h-12",
  big: "h-14",
};

const emit = defineEmits(['update:modelValue']);

const props = defineProps({
    modelValue: {
        type: [Object, Number, String, Array],
        default: null
    },
    options: {
        type: Array,
        default: () => []
    },
    placeholder: {
        type: String,
        default: ''
    },
    displayValue: {
        type: Function,
        default: (option) => option?.title || option?.name || ''
    },
    className: {
        type: String,
        default: ''
    },
    classBorder: {
        type: String,
        default: 'border rounded-lg'
    },
    inputClassName: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    ariaLabel: {
        type: String,
        default: ''
    },
    name: {
        type: String,
        default: ''
    },
    error: {
        type: String,
        default: ''
    },
    shadow: {
        type: Boolean,
        default: false
    },
    disabled: {
        type: Boolean,
        default: false
    },
    required: {
        type: Boolean,
        default: false
    },
    showSelectedCheck: {
        type: Boolean,
        default: true
    },
    multiple: {
        type: Boolean,
        default: false
    },
    variant: {
        type: String,
        default: 'normal' // "normal" | "solid" | "outline" | "line"
    },
    dimension: {
        type: String,
        default: 'medium' // "small" | "medium" | "big"
    },
    allowCustomValue: {
        type: Boolean,
        default: false
    },
    addOptionLabel: {
        type: String,
        default: 'text-add-option'
    },
    onAddOption: {
        type: Function,
        default: () => {}
    }
});

const selectedOption = ref(props.multiple ? [] : null);
const query = ref('');
const inputField = ref(null);
const optionsField = ref(null);
const isOpen = ref(false);
const adding = ref(false);
const isFocus = ref(false);

const updateSelected = (value) => {
  if (props.multiple) {
    const values = Array.isArray(value) ? value : [];
    selectedOption.value = props.options.filter(option => values.includes(option.id));
    return;
  }
  selectedOption.value = _.find(props.options, { id: value }) || null;
  if (_.has(selectedOption.value, 'id') && value === selectedOption.value?.id) {
    emit('update:modelValue', selectedOption.value?.id);
  }
}

watch(() => props.modelValue, updateSelected);
updateSelected(props.modelValue);

const handleUpdate = (value) => {
  emit('update:modelValue', props.multiple
    ? (value || []).map(option => option.id)
    : value?.id ?? null
  );
}

const inputDisplayValue = (value) => {
  if (props.multiple) return isFocus.value ? query.value : '';
  return props.displayValue(value);
}

const selectableOptions = computed(() =>
  props.required
    ? props.options.filter((option) => option?.id !== null)
    : props.options
);

const filteredOptions = computed(() =>
  query.value === ''
    ? selectableOptions.value
    : selectableOptions.value.filter((option) =>
        `${props.displayValue(option)}`
          .toLowerCase()
          .replace(/\s+/g, '')
          .includes(query.value.toLowerCase().replace(/\s+/g, ''))
      )
)

const addOptionValue = () => {
  if (props.allowCustomValue) {
    adding.value = true;
    props.onAddOption(query.value);
    _.delay(() => {
      query.value = '';
      isOpen.value = true;
      adding.value = false;
    }, 100);
  }
}

const handleFocus = _.debounce(() => {
  isFocus.value = true;
  isOpen.value = !isOpen.value;
}, 100);

const handleBlur = _.debounce(() => {
  if (!adding.value) {
    isOpen.value = false;
    query.value = '';
  }
  isFocus.value = false;
}, 100);

const handleToggle = () => {
  if (!isOpen.value) {
    isOpen.value = false;
  } else _.delay(() => {
    isOpen.value = !isOpen.value;
  }, 100);
}

const handleSelect = () => {
  _.delay(() => {
    isOpen.value = true;
  }, 100);
}

const handleType = () => {
  isOpen.value = true;
}

const handleClick = () => {
  _.delay(() => {
    isOpen.value = isFocus.value;
  }, 200);
}

</script>
